import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  useToast,
} from '@/components/ui';
import { useState } from 'react';
import { useMe, useSession } from '@/features/auth/session-context';
import {
  locumContractErrorKind,
  useEndLocumContract,
  useLocumPeople,
  useLocums,
  type LocumContract,
} from '@/features/locum/api';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import { useOptionalUrlState } from '@/lib/url-state';
import { Field, Select } from '@/components/ui';
import { LocumContractEditor } from './LocumContractEditor';
import { ContractSummary } from './MyContractPage';
import styles from './LocumPages.module.css';

function dateLabel(value: string) {
  return new Intl.DateTimeFormat('en-MY', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${value}T00:00:00`));
}

export function LocumManagementPage() {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const contracts = useLocums();
  const [selectedId, setSelectedId] = useOptionalUrlState('drawer');
  const selected =
    contracts.data?.find((contract) => contract.id === selectedId) ?? null;
  const canManage = hasPermission('locums:manage');
  const [adding, setAdding] = useState(false);
  return (
    <PageContainer>
      <Card
        title={t('locum.managementTitle')}
        flush
        actions={
          canManage && (
            <Button size="sm" variant="primary" onClick={() => setAdding(true)}>
              {t('staff.addContract')}
            </Button>
          )
        }
      >
        <DataTable
          caption={t('locum.managementTitle')}
          rows={contracts.data}
          rowKey={(contract) => contract.id}
          loading={contracts.isLoading}
          error={contracts.error}
          onRetry={() => void contracts.refetch()}
          empty={
            <EmptyState
              title={t('locum.noLocums')}
              body={t('locum.noLocumsBody')}
            />
          }
          columns={[
            {
              key: 'name',
              header: t('locum.locum'),
              render: (contract) => (
                <>
                  <strong>{contract.fullName}</strong>
                  <span className={styles.muted}>{contract.email}</span>
                </>
              ),
            },
            {
              key: 'branch',
              header: t('locum.branch'),
              render: (contract) => contract.branchName,
            },
            {
              key: 'period',
              header: t('locum.contractPeriod'),
              render: (contract) =>
                `${dateLabel(contract.startsOn)}–${dateLabel(contract.endsOn)}`,
            },
            {
              key: 'status',
              header: t('locum.contractStatus'),
              render: (contract) => (
                <Badge
                  tone={
                    contract.status === 'active'
                      ? 'success'
                      : contract.status === 'cancelled'
                        ? 'danger'
                        : 'neutral'
                  }
                >
                  {t(`locum.status.${contract.status}`)}
                </Badge>
              ),
            },
            {
              key: 'actions',
              header: '',
              render: (contract) => (
                <Button size="sm" onClick={() => setSelectedId(contract.id)}>
                  {t('locum.viewContract')}
                </Button>
              ),
            },
          ]}
        />
      </Card>
      {adding && <AddContractDialog onClose={() => setAdding(false)} />}
      {selected && (
        <ContractDialog
          contract={selected}
          canManage={canManage}
          onClose={() => setSelectedId(null)}
        />
      )}
    </PageContainer>
  );
}

function ContractDialog({
  contract,
  canManage,
  onClose,
}: {
  contract: LocumContract;
  canManage: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const end = useEndLocumContract();
  const toast = useToast();
  const me = useMe();
  // RL-1800 (decision D26): the manager edits contracts here.
  const [editing, setEditing] = useState(false);
  const endError = isApiError(end.error) ? end.error : undefined;
  const endErrorKind = locumContractErrorKind(end.error);
  const endable =
    canManage &&
    (contract.status === 'active' ||
      contract.status === 'upcoming' ||
      contract.status === 'invited');
  const editable = endable && !!contract.locumUserId;
  return (
    <Dialog
      open
      title={t('locum.contractDetails')}
      onClose={onClose}
      dismissible={!end.isPending}
      footer={
        editing ? undefined : (
          <>
            <Button onClick={onClose}>{t('common.close')}</Button>
            {editable && (
              <Button onClick={() => setEditing(true)}>
                {t('staff.editContract')}
              </Button>
            )}
            {endable && (
              <Button
                variant="danger"
                loading={end.isPending}
                onClick={() =>
                  end.mutate(contract.id, {
                    onSuccess: () => {
                      toast.show({
                        tone: 'success',
                        title: t('locum.contractEnded'),
                      });
                      onClose();
                    },
                  })
                }
              >
                {t('locum.endContract')}
              </Button>
            )}
          </>
        )
      }
    >
      {editing && contract.locumUserId ? (
        <LocumContractEditor
          locumUserId={contract.locumUserId}
          roleBranch={
            me.branches.find((b) => b.id === contract.branchId)?.code ?? null
          }
          contract={contract}
          onCancel={() => setEditing(false)}
          onSaved={onClose}
        />
      ) : (
        <ContractSummary contract={contract} />
      )}
      {endError && (
        <ErrorState
          error={endError}
          body={
            endErrorKind === 'not_found'
              ? t('locum.errors.notFoundManage')
              : endErrorKind === 'invalid_rate'
                ? t('locum.errors.invalidRate')
                : endError.message
          }
          onRetry={() => end.reset()}
        />
      )}
    </Dialog>
  );
}

/** RL-1800: Add contract on the Locums page: pick the locum, then the terms. */
function AddContractDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const me = useMe();
  const people = useLocumPeople();
  const [personId, setPersonId] = useState('');
  const person = people.data?.find((p) => p.id === personId) ?? null;
  const roleBranch = person?.branchIds?.length
    ? (me.branches.find((b) => b.id === person.branchIds![0])?.code ?? null)
    : null;
  return (
    <Dialog open title={t('staff.addContract')} onClose={onClose}>
      {people.error ? (
        <ErrorState
          error={people.error}
          onRetry={() => void people.refetch()}
        />
      ) : (
        <Field label={t('locum.locum')}>
          <Select
            value={personId}
            placeholder={
              people.isLoading ? t('common.loading') : t('locum.chooseLocum')
            }
            options={(people.data ?? []).map((p) => ({
              value: p.id,
              label: `${p.fullName} · ${p.email}`,
            }))}
            onChange={(event) => setPersonId(event.target.value)}
          />
        </Field>
      )}
      {people.data && !people.data.length && (
        <EmptyState
          title={t('locum.noLocumPeople')}
          body={t('locum.noLocumPeopleBody')}
        />
      )}
      {person && (
        <LocumContractEditor
          key={person.id}
          locumUserId={person.id}
          roleBranch={roleBranch}
          allowedBranchIds={person.branchIds}
          contract={null}
          onCancel={onClose}
          onSaved={onClose}
        />
      )}
    </Dialog>
  );
}
